import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import QRCode from "qrcode";
import JSZip from "jszip";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { sotApi } from "api/sot";
import { nhanTrangThaiSot } from "lib/nhanTrangThaiSot";
import DauTrang, { NhanDau } from "components/DauTrang";
import { oTieuDeCot, AN_DUOI_MD, AN_DUOI_SM, chuXuongDong } from "lib/kieuBangDuLieu";
import { useDialogFullManHinh, oDialogContent, oDialogActions } from "lib/dungDialogFullManHinh";
import QrCodeImage from "components/QrCodeImage";
import TaiXuongQr from "components/TaiXuongQr";
import { cmSangPx, layKichThuocQrMacDinhCm, luuKichThuocQrMacDinhCm } from "lib/caiDat";
import { taoAnhQrKemMa } from "lib/qrAnh";
import useTour from "hooks/useTour";
import buocQuanLySot from "tours/quanLySot";

const RONG = { soLuong: 1, khoiLuongToiDa: "", khoiLuongBi: "", ghiChu: "" };
const RONG_SUA_HANG_LOAT = {
  doiKhoiLuong: false,
  khoiLuongToiDa: "",
  doiKhoiLuongBi: false,
  khoiLuongBi: "",
  doiGhiChu: false,
  ghiChu: "",
};

function ThongTinPhu({ sot }) {
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {`${sot.khoiLuongHienTai}/${sot.khoiLuongToiDa} kg · ${sot.soGiaoDich} giao dịch`}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  sot: PropTypes.shape({
    khoiLuongHienTai: PropTypes.number,
    khoiLuongToiDa: PropTypes.number,
    soGiaoDich: PropTypes.number,
  }).isRequired,
};

function QuanLySot() {
  const fullManHinh = useDialogFullManHinh();
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [thongBao, setThongBao] = useState("");

  const [modalTaoMoi, setModalTaoMoi] = useState(null);
  const [sotVuaTao, setSotVuaTao] = useState(null);
  const [xemQr, setXemQr] = useState(null);
  const [modalSua, setModalSua] = useState(null);
  const [modalSuaHangLoat, setModalSuaHangLoat] = useState(null);
  const [dangLuu, setDangLuu] = useState(false);

  const [daChon, setDaChon] = useState(() => new Set());
  const [kichThuocHangLoatCm, setKichThuocHangLoatCm] = useState(layKichThuocQrMacDinhCm);
  const [dangXuLyHangLoat, setDangXuLyHangLoat] = useState(false);
  const [daCopyId, setDaCopyId] = useState(null);

  async function saoChepMaSot(maSot, id) {
    try {
      await navigator.clipboard.writeText(maSot);
      setDaCopyId(id);
      setTimeout(() => setDaCopyId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      setLoi("Không sao chép được — trình duyệt chặn quyền clipboard.");
    }
  }

  async function taiLai() {
    // Realtime onSnapshot cập nhật danh sách.
  }

  useEffect(() => {
    setDangTai(true);
    const huy = sotApi.langNgheDanhSach(
      null,
      (ds) => {
        setDanhSach(ds);
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huy;
  }, []);

  async function taoSot(e) {
    e.preventDefault();
    setDangLuu(true);
    setLoi("");
    setThongBao("");
    try {
      const soLuong = Math.max(1, Number(modalTaoMoi.soLuong) || 1);
      const daTao = [];
      for (let i = 0; i < soLuong; i += 1) {
        // Tạo tuần tự để hiện tiến độ / chọn sẵn các Sọt vừa tạo; mã Sọt
        // (LV + 4 ký tự random) unique nhờ maSotDaCap trong sotApi.taoMoi.
        // eslint-disable-next-line no-await-in-loop
        const sot = await sotApi.taoMoi(modalTaoMoi);
        daTao.push(sot);
      }
      setModalTaoMoi(null);
      await taiLai();
      if (soLuong === 1) {
        setSotVuaTao(daTao[0]);
      } else {
        // Chọn sẵn các Sọt vừa tạo để dùng luôn nút "Tải xuống hàng loạt"
        // / "In hàng loạt" đã có, không cần dựng lại màn hình xem N mã QR.
        setDaChon(new Set(daTao.map((p) => p.id)));
        setThongBao(
          `Đã tạo ${soLuong} Sọt mới — đã chọn sẵn, dùng "Tải xuống hàng loạt" hoặc "In hàng loạt" bên dưới để lấy mã QR.`
        );
      }
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  async function suaHangLoat(e) {
    e.preventDefault();
    setDangXuLyHangLoat(true);
    setLoi("");
    setThongBao("");
    try {
      const boQua = [];
      for (const p of sotDaChon) {
        const klMoi = modalSuaHangLoat.doiKhoiLuong
          ? Number(modalSuaHangLoat.khoiLuongToiDa)
          : p.khoiLuongToiDa;
        if (modalSuaHangLoat.doiKhoiLuong && klMoi < p.khoiLuongHienTai) {
          boQua.push(p.maSot);
        } else {
          const gcMoi = modalSuaHangLoat.doiGhiChu ? modalSuaHangLoat.ghiChu : p.ghiChu;
          const biMoi = modalSuaHangLoat.doiKhoiLuongBi
            ? modalSuaHangLoat.khoiLuongBi
            : p.khoiLuongBi ?? null;
          // eslint-disable-next-line no-await-in-loop
          await sotApi.capNhat(p.id, {
            khoiLuongToiDa: klMoi,
            khoiLuongBi: biMoi,
            ghiChu: gcMoi,
          });
        }
      }
      setModalSuaHangLoat(null);
      await taiLai();
      const daCapNhat = sotDaChon.length - boQua.length;
      setThongBao(
        boQua.length > 0
          ? `Đã cập nhật ${daCapNhat} Sọt — bỏ qua ${
              boQua.length
            } Sọt vì khối lượng chứa tối đa mới nhỏ hơn khối lượng đã thu mua: ${boQua.join(", ")}.`
          : `Đã cập nhật ${daCapNhat} Sọt.`
      );
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function suaSot(e) {
    e.preventDefault();
    if (Number(modalSua.khoiLuongToiDa) < modalSua.khoiLuongHienTai) {
      setLoi(
        `Khối lượng chứa tối đa không được nhỏ hơn khối lượng đã thu mua hiện tại (${modalSua.khoiLuongHienTai}kg).`
      );
      return;
    }
    setDangLuu(true);
    setLoi("");
    try {
      await sotApi.capNhat(modalSua.id, modalSua);
      setModalSua(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  function chonMotSot(id) {
    setDaChon((s) => {
      const moi = new Set(s);
      if (moi.has(id)) moi.delete(id);
      else moi.add(id);
      return moi;
    });
  }

  function chonTatCa() {
    setDaChon((s) => (s.size === danhSach.length ? new Set() : new Set(danhSach.map((p) => p.id))));
  }

  const sotDaChon = danhSach.filter((p) => daChon.has(p.id));

  // Sọt tạo thẳng ngoài app (vd. sửa tay trên Firebase Console) có thể
  // thiếu qrCode — bỏ qua thay vì để cả batch lỗi, và báo cho người dùng biết
  // đã bỏ qua Sọt nào.
  function tachSotCoQr() {
    return {
      coQr: sotDaChon.filter((p) => p.qrCode),
      thieuQr: sotDaChon.filter((p) => !p.qrCode),
    };
  }

  // Cùng dùng chung "kích thước QR mặc định" (cm) với nút tải 1 mã (TaiXuongQr).
  async function taiXuongHangLoat() {
    const { coQr, thieuQr } = tachSotCoQr();
    if (coQr.length === 0) {
      setLoi("Không có Sọt nào trong lựa chọn có mã QR để tải xuống.");
      return;
    }
    setDangXuLyHangLoat(true);
    setLoi(
      thieuQr.length > 0
        ? `Đã bỏ qua ${thieuQr.length} Sọt thiếu mã QR: ${thieuQr.map((p) => p.maSot).join(", ")}.`
        : ""
    );
    try {
      const cm = Number(kichThuocHangLoatCm) || 4;
      luuKichThuocQrMacDinhCm(cm);
      const px = cmSangPx(cm);
      const zip = new JSZip();
      for (const p of coQr) {
        const dataUrl = await taoAnhQrKemMa(p.qrCode, px);
        zip.file(`${p.maSot}.png`, dataUrl.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-sot-${new Date().toISOString().slice(0, 10)}.zip`;
      // Phải gắn vào DOM rồi mới click() để trình duyệt chắc chắn nhận thao
      // tác tải xuống; revoke URL trễ 1 nhịp vì click() kích hoạt tải xuống
      // bất đồng bộ — revoke ngay lập tức có thể huỷ blob trước khi trình
      // duyệt kịp đọc, làm file tải về rỗng/hỏng.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function inHangLoat() {
    const { coQr, thieuQr } = tachSotCoQr();
    if (coQr.length === 0) {
      setLoi("Không có Sọt nào trong lựa chọn có mã QR để in.");
      return;
    }
    setDangXuLyHangLoat(true);
    setLoi(
      thieuQr.length > 0
        ? `Đã bỏ qua ${thieuQr.length} Sọt thiếu mã QR: ${thieuQr.map((p) => p.maSot).join(", ")}.`
        : ""
    );
    try {
      const cuaSo = window.open("", "_blank");
      if (!cuaSo) {
        setLoi("Trình duyệt đã chặn cửa sổ in — cho phép popup cho trang này rồi thử lại.");
        return;
      }
      const cm = Number(kichThuocHangLoatCm) || 4;
      luuKichThuocQrMacDinhCm(cm);
      const px = cmSangPx(cm);
      const items = await Promise.all(
        coQr.map(async (p) => ({
          ma: p.maSot,
          src: await QRCode.toDataURL(p.qrCode, { width: px, margin: 1 }),
        }))
      );
      const canhCm = cm;
      const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="utf-8" />
<title>In QR Sot hang loat</title>
<style>
  body { font-family: Arial, sans-serif; margin: 1cm; }
  .luoi { display: flex; flex-wrap: wrap; gap: 0.5cm; }
  .nhan { width: ${canhCm}cm; text-align: center; page-break-inside: avoid; }
  .nhan img { width: ${canhCm}cm; height: ${canhCm}cm; display: block; }
  .nhan span { display: block; font-weight: bold; margin-top: 2mm; font-size: 3.5mm; }
</style>
</head><body>
<div class="luoi">
${items
  .map((i) => `<div class="nhan"><img src="${i.src}" alt="${i.ma}" /><span>${i.ma}</span></div>`)
  .join("")}
</div>
<script>window.onload = () => window.print();</script>
</body></html>`;
      cuaSo.document.write(html);
      cuaSo.document.close();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  useTour("quan-ly-sot", buocQuanLySot());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Kho"
          tieuDe="Sọt"
          moTa="Tạo Sọt mới để in mã QR, theo dõi khối lượng và trạng thái từng Sọt."
        >
          <MDButton
            id="tour-quan-ly-sot-tao-moi"
            variant="gradient"
            color="info"
            onClick={() => setModalTaoMoi({ ...RONG })}
          >
            <Icon sx={{ mr: 0.75 }}>add</Icon>
            Tạo Sọt mới
          </MDButton>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
        {thongBao && (
          <MDBox mb={2}>
            <MDAlert color="success">{thongBao}</MDAlert>
          </MDBox>
        )}

        {daChon.size > 0 && (
          <Card sx={{ mb: 2, p: 2 }}>
            <MDBox display="flex" alignItems="flex-end" flexWrap="wrap" gap={2}>
              <MDBox sx={{ alignSelf: "center" }}>
                <MDTypography variant="overline" color="text" lineHeight={1} display="block">
                  Đang chọn
                </MDTypography>
                <MDTypography variant="button" fontWeight="medium">
                  {daChon.size} Sọt
                </MDTypography>
              </MDBox>
              <MDBox width="10rem">
                <MDInput
                  type="number"
                  label="Kích thước QR (cm)"
                  value={kichThuocHangLoatCm}
                  onChange={(e) => setKichThuocHangLoatCm(e.target.value)}
                  inputProps={{ min: 1, max: 20, step: 0.5 }}
                  fullWidth
                />
              </MDBox>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={taiXuongHangLoat}
                disabled={dangXuLyHangLoat}
              >
                {dangXuLyHangLoat ? "Đang xử lý..." : "Tải xuống hàng loạt (.zip)"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={inHangLoat}
                disabled={dangXuLyHangLoat}
              >
                {dangXuLyHangLoat ? "Đang xử lý..." : "In hàng loạt"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => setModalSuaHangLoat({ ...RONG_SUA_HANG_LOAT })}
                disabled={dangXuLyHangLoat}
              >
                Sửa hàng loạt
              </MDButton>
              <MDButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => setDaChon(new Set())}
              >
                Bỏ chọn
              </MDButton>
            </MDBox>
          </Card>
        )}

        <Card id="tour-quan-ly-sot-bang">
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <MDBox>
              <NhanDau>Danh sách</NhanDau>
              <MDTypography variant="h6">Sọt đang theo dõi</MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text">
              {danhSach.length} Sọt
            </MDTypography>
          </MDBox>

          {dangTai ? (
            <MDBox px={2.5} pb={3}>
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            </MDBox>
          ) : danhSach.length === 0 ? (
            <MDBox px={2.5} pb={4} pt={1} textAlign="center">
              <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                inventory_2
              </Icon>
              <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                Chưa có Sọt nào.
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                Bấm “Tạo Sọt mới” để sinh mã Sọt và mã QR đầu tiên.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={[oTieuDeCot, AN_DUOI_SM]}>
                      <Checkbox
                        checked={daChon.size > 0 && daChon.size === danhSach.length}
                        indeterminate={daChon.size > 0 && daChon.size < danhSach.length}
                        onChange={chonTatCa}
                      />
                    </TableCell>
                    <TableCell sx={oTieuDeCot}>Mã Sọt</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Khối lượng</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Số giao dịch</TableCell>
                    <TableCell sx={oTieuDeCot}>Trạng thái</TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {danhSach.map((p) => {
                    const nhan = nhanTrangThaiSot(p.trangThai);
                    return (
                      <TableRow
                        key={p.id}
                        sx={({ palette }) => ({
                          transition: "background-color 160ms ease",
                          "&:hover": { backgroundColor: palette.grey[100] },
                        })}
                      >
                        <TableCell padding="checkbox" sx={AN_DUOI_SM}>
                          <Checkbox checked={daChon.has(p.id)} onChange={() => chonMotSot(p.id)} />
                        </TableCell>
                        <TableCell sx={chuXuongDong}>
                          <MDBox display="flex" alignItems="center" gap={0.25}>
                            <MDTypography
                              component={Link}
                              to={`/sot/${p.id}`}
                              variant="button"
                              fontWeight="medium"
                              color="info"
                              sx={chuXuongDong}
                            >
                              {p.maSot}
                            </MDTypography>
                            <IconButton
                              size="small"
                              color={daCopyId === p.id ? "success" : "secondary"}
                              onClick={() => saoChepMaSot(p.maSot, p.id)}
                              sx={{ p: 0.25 }}
                            >
                              <Icon fontSize="small" sx={{ fontSize: "0.95rem !important" }}>
                                {daCopyId === p.id ? "check" : "content_copy"}
                              </Icon>
                            </IconButton>
                          </MDBox>
                          <ThongTinPhu sot={p} />
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>
                          {p.khoiLuongHienTai} / {p.khoiLuongToiDa} kg
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>{p.soGiaoDich}</TableCell>
                        <TableCell>
                          <MDBadge
                            badgeContent={nhan.label}
                            color={nhan.color}
                            variant="gradient"
                            size="sm"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <MDBox display="flex" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
                            <MDButton
                              variant="text"
                              color="info"
                              size="small"
                              onClick={() => setXemQr(p)}
                            >
                              Xem QR
                            </MDButton>
                            <MDButton
                              variant="text"
                              color="dark"
                              size="small"
                              onClick={() =>
                                setModalSua({
                                  id: p.id,
                                  khoiLuongToiDa: p.khoiLuongToiDa,
                                  khoiLuongBi: p.khoiLuongBi ?? "",
                                  khoiLuongHienTai: p.khoiLuongHienTai,
                                  ghiChu: p.ghiChu ?? "",
                                })
                              }
                            >
                              Sửa
                            </MDButton>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      <Dialog
        open={!!modalTaoMoi}
        onClose={() => setModalTaoMoi(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle component="div" sx={{ pb: 0.5 }}>
          <NhanDau>Thêm mới</NhanDau>
          <MDTypography variant="h5" component="h2">
            Tạo Sọt
          </MDTypography>
        </DialogTitle>
        <MDBox component="form" onSubmit={taoSot}>
          <DialogContent sx={oDialogContent}>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Số lượng Sọt"
                value={modalTaoMoi?.soLuong ?? 1}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, soLuong: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 1, max: 200, step: 1 }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Khối lượng chứa tối đa (kg)"
                value={modalTaoMoi?.khoiLuongToiDa ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, khoiLuongToiDa: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Khối lượng bì (kg)"
                value={modalTaoMoi?.khoiLuongBi ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, khoiLuongBi: e.target.value })}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
              />
            </MDBox>
            <MDBox mb={1}>
              <MDInput
                label="Ghi chú"
                value={modalTaoMoi?.ghiChu ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, ghiChu: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </MDBox>
            <MDTypography variant="caption" color="text">
              Mã Sọt và mã QR sẽ được hệ thống tự sinh cho từng Sọt — tạo nhiều Sọt cùng lúc thì tất
              cả dùng chung khối lượng chứa tối đa/khối lượng bì/ghi chú ở trên. Khối lượng bì (khối
              lượng vỏ Sọt) không bắt buộc, chỉ lưu để tham khảo.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={oDialogActions}>
            <MDButton variant="outlined" color="secondary" onClick={() => setModalTaoMoi(null)}>
              Huỷ
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
              {dangLuu ? "Đang tạo..." : "Tạo Sọt"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>

      <Dialog
        open={!!modalSua}
        onClose={() => setModalSua(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle component="div" sx={{ pb: 0.5 }}>
          <NhanDau>Cập nhật</NhanDau>
          <MDTypography variant="h5" component="h2">
            Thông tin Sọt
          </MDTypography>
        </DialogTitle>
        <MDBox component="form" onSubmit={suaSot}>
          <DialogContent sx={oDialogContent}>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Khối lượng chứa tối đa (kg)"
                value={modalSua?.khoiLuongToiDa ?? ""}
                onChange={(e) => setModalSua({ ...modalSua, khoiLuongToiDa: e.target.value })}
                fullWidth
                required
                inputProps={{ min: modalSua?.khoiLuongHienTai ?? 0, step: 1 }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="number"
                label="Khối lượng bì (kg)"
                value={modalSua?.khoiLuongBi ?? ""}
                onChange={(e) => setModalSua({ ...modalSua, khoiLuongBi: e.target.value })}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
              />
            </MDBox>
            <MDBox mb={1}>
              <MDInput
                label="Ghi chú"
                value={modalSua?.ghiChu ?? ""}
                onChange={(e) => setModalSua({ ...modalSua, ghiChu: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </MDBox>
            <MDTypography variant="caption" color="text">
              Đã thu mua {modalSua?.khoiLuongHienTai}kg — không thể đặt khối lượng chứa tối đa thấp
              hơn mức này. Để trống khối lượng bì nếu không cần theo dõi.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={oDialogActions}>
            <MDButton variant="outlined" color="secondary" onClick={() => setModalSua(null)}>
              Huỷ
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
              {dangLuu ? "Đang lưu..." : "Lưu thay đổi"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>

      <Dialog
        open={!!modalSuaHangLoat}
        onClose={() => setModalSuaHangLoat(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle component="div" sx={{ pb: 0.5 }}>
          <NhanDau>Sửa hàng loạt</NhanDau>
          <MDTypography variant="h5" component="h2">
            {daChon.size} Sọt đang chọn
          </MDTypography>
        </DialogTitle>
        <MDBox component="form" onSubmit={suaHangLoat}>
          <DialogContent sx={oDialogContent}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={modalSuaHangLoat?.doiKhoiLuong ?? false}
                onChange={(e) =>
                  setModalSuaHangLoat({ ...modalSuaHangLoat, doiKhoiLuong: e.target.checked })
                }
              />
              <MDTypography variant="button">Đổi khối lượng chứa tối đa</MDTypography>
            </MDBox>
            {modalSuaHangLoat?.doiKhoiLuong && (
              <MDBox mb={2} ml={4}>
                <MDInput
                  type="number"
                  label="Khối lượng chứa tối đa (kg)"
                  value={modalSuaHangLoat?.khoiLuongToiDa ?? ""}
                  onChange={(e) =>
                    setModalSuaHangLoat({ ...modalSuaHangLoat, khoiLuongToiDa: e.target.value })
                  }
                  fullWidth
                  required
                  inputProps={{ min: 1, step: 1 }}
                />
              </MDBox>
            )}

            <MDBox display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={modalSuaHangLoat?.doiKhoiLuongBi ?? false}
                onChange={(e) =>
                  setModalSuaHangLoat({ ...modalSuaHangLoat, doiKhoiLuongBi: e.target.checked })
                }
              />
              <MDTypography variant="button">Đổi khối lượng bì</MDTypography>
            </MDBox>
            {modalSuaHangLoat?.doiKhoiLuongBi && (
              <MDBox mb={2} ml={4}>
                <MDInput
                  type="number"
                  label="Khối lượng bì (kg)"
                  value={modalSuaHangLoat?.khoiLuongBi ?? ""}
                  onChange={(e) =>
                    setModalSuaHangLoat({ ...modalSuaHangLoat, khoiLuongBi: e.target.value })
                  }
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
                <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                  Để trống nếu muốn xoá khối lượng bì trên các Sọt đã chọn.
                </MDTypography>
              </MDBox>
            )}

            <MDBox display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={modalSuaHangLoat?.doiGhiChu ?? false}
                onChange={(e) =>
                  setModalSuaHangLoat({ ...modalSuaHangLoat, doiGhiChu: e.target.checked })
                }
              />
              <MDTypography variant="button">Đổi ghi chú</MDTypography>
            </MDBox>
            {modalSuaHangLoat?.doiGhiChu && (
              <MDBox mb={1} ml={4}>
                <MDInput
                  label="Ghi chú"
                  value={modalSuaHangLoat?.ghiChu ?? ""}
                  onChange={(e) =>
                    setModalSuaHangLoat({ ...modalSuaHangLoat, ghiChu: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={2}
                />
              </MDBox>
            )}

            <MDTypography variant="caption" color="text">
              Sọt nào có khối lượng chứa tối đa mới nhỏ hơn khối lượng đã thu mua sẽ tự động được bỏ
              qua.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={oDialogActions}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setModalSuaHangLoat(null)}
            >
              Huỷ
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={
                dangXuLyHangLoat ||
                !(
                  modalSuaHangLoat?.doiKhoiLuong ||
                  modalSuaHangLoat?.doiKhoiLuongBi ||
                  modalSuaHangLoat?.doiGhiChu
                )
              }
            >
              {dangXuLyHangLoat ? "Đang lưu..." : "Áp dụng"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>

      <Dialog
        open={!!sotVuaTao}
        onClose={() => setSotVuaTao(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle component="div" sx={{ pb: 0.5 }}>
          <NhanDau>Đã tạo</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            Sọt {sotVuaTao?.maSot}
          </MDTypography>
          <IconButton
            onClick={() => setSotVuaTao(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...oDialogContent, pt: 1 }}>
          <MDTypography variant="button" color="text" mb={2} display="block">
            In mã QR này và dán lên Sọt để nhân viên quét khi thu mua.
          </MDTypography>
          {sotVuaTao && (
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <QrCodeImage value={sotVuaTao.qrCode} />
              <TaiXuongQr value={sotVuaTao.qrCode} tenFile={`sot-${sotVuaTao.maSot}`} />
            </MDBox>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!xemQr}
        onClose={() => setXemQr(null)}
        fullScreen={fullManHinh}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle component="div" sx={{ pb: 0.5 }}>
          <NhanDau>Mã QR</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            Sọt {xemQr?.maSot}
          </MDTypography>
          <IconButton
            onClick={() => setXemQr(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...oDialogContent, pt: 1 }}>
          {xemQr && (
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <QrCodeImage value={xemQr.qrCode} />
              <TaiXuongQr value={xemQr.qrCode} tenFile={`sot-${xemQr.maSot}`} />
            </MDBox>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default QuanLySot;
